import React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { useReceivableTerm } from 'hooks/useReceivableTerm';

const CustomLegend = ({ payload, term }) => {
  if (!payload || !payload.length) return null;

  const pluralLabel = term?.plural ?? 'Títulos';

  // Agrupar itens da legenda por empresa (reconhecer Títulos ou Mensalidades)
  const itensAgrupados = {};
  const isTitulosEntry = (value) => value.includes('Títulos') || value.includes('Mensalidades');

  payload.forEach((entry) => {
    const isValor = entry.value.includes('Valor');
    const isTitulos = isTitulosEntry(entry.value);
    const isConsolidado = entry.value.includes('Consolidado');

    if (isConsolidado) {
      if (!itensAgrupados['Consolidado']) {
        itensAgrupados['Consolidado'] = {};
      }
      if (isValor) {
        itensAgrupados['Consolidado'].valor = entry;
      }
      if (isTitulos) {
        itensAgrupados['Consolidado'].titulos = entry;
      }
    } else {
      const nomeEmpresa = entry.value.replace(/ - (Valor|Títulos|Mensalidades)/g, '');
      if (!itensAgrupados[nomeEmpresa]) {
        itensAgrupados[nomeEmpresa] = {};
      }
      if (isValor) {
        itensAgrupados[nomeEmpresa].valor = entry;
      }
      if (isTitulos) {
        itensAgrupados[nomeEmpresa].titulos = entry;
      }
    }
  });

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '16px',
      fontSize: '10px'
    }}>
      {itensAgrupados['Consolidado'] && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {itensAgrupados['Consolidado'].valor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: 8,
                height: 8,
                backgroundColor: itensAgrupados['Consolidado'].valor.color,
                borderRadius: '2px'
              }}></div>
              <span>Consolidado - Valor</span>
            </div>
          )}
          {itensAgrupados['Consolidado'].titulos && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: 8,
                height: 2,
                backgroundColor: itensAgrupados['Consolidado'].titulos.color,
                borderRadius: '1px'
              }}></div>
              <span>Consolidado - {pluralLabel}</span>
            </div>
          )}
        </div>
      )}
      {Object.keys(itensAgrupados)
        .filter(empresa => empresa !== 'Consolidado')
        .map((empresa) => (
          <div key={empresa} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {itensAgrupados[empresa].valor && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{
                  width: 8,
                  height: 8,
                  backgroundColor: itensAgrupados[empresa].valor.color,
                  borderRadius: '2px'
                }}></div>
                <span>{empresa} - Valor</span>
              </div>
            )}
            {itensAgrupados[empresa].titulos && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{
                  width: 8,
                  height: 2,
                  backgroundColor: itensAgrupados[empresa].titulos.color,
                  borderRadius: '1px'
                }}></div>
                <span>{empresa} - {pluralLabel}</span>
              </div>
            )}
          </div>
        ))}
    </div>
  );
};

const CustomTooltip = ({ active, payload, label, term }) => {
  if (!active || !payload || !payload.length) return null;

  const pluralLower = term?.pluralLower ?? 'títulos';
  const pluralLabel = term?.plural ?? 'Títulos';

  // Agrupar dados por empresa para evitar duplicações
  const dadosAgrupados = {};
  payload.forEach((entry) => {
    const isValor = entry.dataKey.includes('valor');
    const isTitulos = entry.dataKey.includes('titulos');
    const isConsolidado = entry.dataKey.includes('Consolidado');
    
    if (isConsolidado) {
      if (!dadosAgrupados['Consolidado']) {
        dadosAgrupados['Consolidado'] = {};
      }
      if (isValor) {
        dadosAgrupados['Consolidado'].valor = entry.value;
        dadosAgrupados['Consolidado'].cor = entry.color;
      }
      if (isTitulos) {
        dadosAgrupados['Consolidado'].titulos = entry.value;
      }
    } else {
      // Extrair nome da empresa do dataKey
      const nomeEmpresa = entry.dataKey.replace(/valor|titulos/g, '').replace(/([A-Z])/g, ' $1').trim();
      
      if (!dadosAgrupados[nomeEmpresa]) {
        dadosAgrupados[nomeEmpresa] = {};
      }
      if (isValor) {
        dadosAgrupados[nomeEmpresa].valor = entry.value;
        dadosAgrupados[nomeEmpresa].cor = entry.color;
      }
      if (isTitulos) {
        dadosAgrupados[nomeEmpresa].titulos = entry.value;
      }
    }
  });

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: 12,
      fontSize: 12,
      boxShadow: 'var(--shadow)',
      color: 'var(--text-primary)'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: 8, color: 'var(--text-primary)' }}>
        {formatMes(label)}
      </div>
      
      {/* Exibir dados consolidados primeiro */}
      {dadosAgrupados['Consolidado'] && (
        <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
          <div style={{ 
            color: dadosAgrupados['Consolidado'].cor, 
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <div style={{
              width: 8,
              height: 8,
              backgroundColor: dadosAgrupados['Consolidado'].cor,
              borderRadius: '50%'
            }}></div>
              <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>Consolidado - Valor:</span>
            <span>R$ {dadosAgrupados['Consolidado'].valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}</span>
          </div>
          <div style={{ 
            color: dadosAgrupados['Consolidado'].cor, 
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <div style={{
              width: 8,
              height: 8,
              backgroundColor: dadosAgrupados['Consolidado'].cor,
              borderRadius: '50%'
            }}></div>
              <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>Consolidado - {pluralLabel}:</span>
            <span>{dadosAgrupados['Consolidado'].titulos?.toLocaleString('pt-BR') || '0'} {pluralLower}</span>
          </div>
        </div>
      )}
      
      {/* Exibir dados de cada empresa */}
      {Object.keys(dadosAgrupados)
        .filter(empresa => empresa !== 'Consolidado')
        .map((empresa) => (
          <div key={empresa} style={{ marginBottom: 4 }}>
            <div style={{
              color: dadosAgrupados[empresa].cor,
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <div style={{
                width: 8,
                height: 8,
                backgroundColor: dadosAgrupados[empresa].cor,
                borderRadius: '50%'
              }}></div>
              <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>{empresa} - Valor:</span>
              <span>R$ {dadosAgrupados[empresa].valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}</span>
            </div>
            <div style={{
              color: dadosAgrupados[empresa].cor,
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <div style={{
                width: 8,
                height: 8,
                backgroundColor: dadosAgrupados[empresa].cor,
                borderRadius: '50%'
              }}></div>
              <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>{empresa} - {pluralLabel}:</span>
              <span>{dadosAgrupados[empresa].titulos?.toLocaleString('pt-BR') || '0'} {pluralLower}</span>
            </div>
          </div>
        ))}
    </div>
  );
};

// Constante de meses
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Funções auxiliares para formatação
const formatMes = (value) => {
  if (!value) return '';
  
  if (MESES.some(mes => value.includes(mes))) {
    return value;
  }
  
  if (value.includes('-')) {
    const [ano, mes] = value.split('-');
    const mesIndex = parseInt(mes, 10) - 1;
    
    if (mesIndex >= 0 && mesIndex < 12) {
      return `${MESES[mesIndex]}/${ano.slice(2)}`;
    }
  }
  
  return value;
};

const formatValor = (value) => {
  if (value >= 1000000) return `R$ ${(value/1000000).toFixed(1)}M`;
  return `R$ ${(value/1000).toFixed(0)}k`;
};

const formatTitulos = (value) => {
  if (value >= 1000000) return `${(value/1000000).toFixed(1)}M`;
  return `${(value/1000).toFixed(0)}k`;
};

// Tooltip customizado para o gráfico de crescimento
const CustomTooltipGrowth = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: 12,
      fontSize: 12,
      boxShadow: 'var(--shadow)',
      color: 'var(--text-primary)'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: 8, color: 'var(--text-primary)' }}>
        {formatMes(label)}
      </div>
      
      {payload.map((entry, index) => {
        const isConsolidado = entry.dataKey === 'crescimentoConsolidado';
        const isEmpresa = entry.dataKey.includes('crescimento') && !isConsolidado;
        
        if (!isConsolidado && !isEmpresa) return null;
        
        let labelText = '';
        if (isConsolidado) {
          labelText = 'Consolidado - Crescimento';
        } else if (isEmpresa) {
          // Extrair nome da empresa do dataKey
          const nomeEmpresa = entry.dataKey.replace('crescimento', '').replace(/([A-Z])/g, ' $1').trim();
          labelText = `${nomeEmpresa} - Crescimento`;
        }
        
        return (
          <div key={index} style={{ 
            color: entry.stroke || entry.color, 
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <div style={{
              width: 8,
              height: 8,
              backgroundColor: entry.stroke || entry.color,
              borderRadius: '50%'
            }}></div>
            <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>{labelText}:</span>
            <span>{parseFloat(entry.value).toFixed(2)}%</span>
          </div>
        );
      })}
    </div>
  );
};

const GraficoConsolidado = ({ dadosGrafico, loading = false, accentColor = '#3b82f6' }) => {
  const term = useReceivableTerm();
  const [visualizacao, setVisualizacao] = React.useState('geral'); // 'geral' ou 'crescimento'

  // Processar dados para o formato do gráfico
  const dadosProcessados = React.useMemo(() => {
    
    if (!dadosGrafico || !dadosGrafico.consolidado || !dadosGrafico.empresas) {
      return [];
    }


    // Obter todos os meses únicos
    const todosMeses = new Set();
    
    // Adicionar meses do consolidado
    dadosGrafico.consolidado.dados.forEach(item => {
      todosMeses.add(item.mes);
    });
    
    // Adicionar meses das empresas
    dadosGrafico.empresas.forEach(empresa => {
      empresa.dados.forEach(item => {
        todosMeses.add(item.mes);
      });
    });

    // Converter para array e ordenar
    const mesesOrdenados = Array.from(todosMeses).sort();

    // Criar estrutura de dados para cada mês
    
    const dadosCombinados = mesesOrdenados.map(mes => {
      
      const dadosMes = {
        mes: formatMes(mes)
      };

      // Dados consolidados
      const consolidado = dadosGrafico.consolidado.dados.find(item => item.mes === mes);
      if (consolidado) {
        dadosMes.valorConsolidado = parseFloat(consolidado.valor_recebido) || 0;
        dadosMes.titulosConsolidado = parseInt(consolidado.titulos_recebidos) || 0;
      }

      // Dados de cada empresa
      dadosGrafico.empresas.forEach(empresa => {
        const dadosEmpresa = empresa.dados.find(item => item.mes === mes);
        if (dadosEmpresa) {
          dadosMes[`valor${empresa.nome.replace(/\s+/g, '')}`] = parseFloat(dadosEmpresa.valor_recebido) || 0;
          dadosMes[`titulos${empresa.nome.replace(/\s+/g, '')}`] = parseInt(dadosEmpresa.titulos_recebidos) || 0;
        }
      });

      return dadosMes;
    });
    

    // Calcular crescimento percentual para cada mês
    const dadosComCrescimento = dadosCombinados.map((item, index) => {
      const dadosComCrescimentoItem = { ...item };

      // Calcular crescimento consolidado
      if (index > 0) {
        const valorAnterior = dadosCombinados[index - 1].valorConsolidado || 0;
        const valorAtual = item.valorConsolidado || 0;
        
        if (valorAnterior > 0) {
          dadosComCrescimentoItem.crescimentoConsolidado = ((valorAtual - valorAnterior) / valorAnterior) * 100;
        } else {
          dadosComCrescimentoItem.crescimentoConsolidado = 0;
        }
      } else {
        dadosComCrescimentoItem.crescimentoConsolidado = 0;
      }

      // Calcular crescimento para cada empresa
      dadosGrafico.empresas.forEach(empresa => {
        const nomeEmpresa = empresa.nome.replace(/\s+/g, '');
        const valorAtual = item[`valor${nomeEmpresa}`] || 0;
        
        if (index > 0) {
            const valorAnterior = dadosCombinados[index - 1][`valor${nomeEmpresa}`] || 0;
          
          if (valorAnterior > 0) {
            const crescimento = ((valorAtual - valorAnterior) / valorAnterior) * 100;
            dadosComCrescimentoItem[`crescimento${nomeEmpresa}`] = crescimento;
          } else {
            dadosComCrescimentoItem[`crescimento${nomeEmpresa}`] = 0;
          }
        } else {
          dadosComCrescimentoItem[`crescimento${nomeEmpresa}`] = 0;
        }
      });

      return dadosComCrescimentoItem;
    });

    return dadosComCrescimento;
  }, [dadosGrafico]);

  if (loading) {
    return (
      <div className="p-4 h-full min-h-[420px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur animate-pulse">
        <div className="h-3 w-24 bg-[color:var(--surface-muted)] rounded mb-3"></div>
        <div className="h-5 w-40 bg-[color:var(--surface-muted)] rounded mb-2"></div>
        <div className="h-3 w-56 bg-[color:var(--surface-muted)] rounded mb-6"></div>
        <div className="h-56 w-full bg-[color:var(--surface-muted)] rounded"></div>
      </div>
    );
  }

  if (!dadosProcessados || dadosProcessados.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center text-[color:var(--text-muted)]">
          <div className="w-16 h-16 mx-auto mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2 text-[color:var(--text-primary)]">Dados do Gráfico</h3>
          <p>Carregando dados de evolução mensal...</p>
        </div>
      </div>
    );
  }

  // Obter cores das empresas
  const coresEmpresas = dadosGrafico.empresas.map(empresa => ({
    nome: empresa.nome,
    cor: empresa.cor
  }));

  return (
    <div className="p-4 h-full min-h-[420px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur transition-colors duration-200 hover:bg-[color:var(--surface-muted)]">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-left">
        <div className="text-[11px] font-semibold tracking-[0.35em] text-[color:var(--text-muted)] uppercase">
          Evolucao
        </div>
        <h2 className="text-base font-semibold text-[color:var(--text-primary)] mt-1 tracking-tight">Evolucao Consolidada</h2>
        <p className="text-xs text-[color:var(--text-muted)] mt-1">Comparativo por empresa e consolidado</p>
      </div>

      {/* Botões de alternância */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setVisualizacao('geral')}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
            visualizacao === 'geral'
              ? 'text-white'
              : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]'
          }`}
          style={visualizacao === 'geral' ? { background: accentColor } : undefined}
        >
          Visão Geral
        </button>
        <button
          onClick={() => setVisualizacao('crescimento')}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
            visualizacao === 'crescimento'
              ? 'text-white'
              : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]'
          }`}
          style={visualizacao === 'crescimento' ? { background: accentColor } : undefined}
        >
          Foco Crescimento
        </button>
      </div>
    </div>

    <div className="h-64 lg:h-72 mb-2 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          {visualizacao === 'geral' ? (
            <ComposedChart
              data={dadosProcessados}
              margin={{ top: 20, right: 30, left: 20, bottom: 35 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
              <XAxis 
                dataKey="mes" 
                stroke="#94a3b8"
                fontSize={10}
                tickLine={false}
                axisLine={{ stroke: 'rgba(148,163,184,0.35)' }}
                tickFormatter={formatMes}
              />
              <YAxis 
                yAxisId="left"
                stroke="#60a5fa"
                fontSize={9}
                tickFormatter={formatValor}
                label={{ value: 'Valores', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 9 } }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="#34d399"
                fontSize={9}
                tickFormatter={formatTitulos}
                label={{ value: term.plural, angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fontSize: 9 } }}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} term={term} />} />
              <Legend
                content={(props) => <CustomLegend {...props} term={term} />}
                wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
              
              {/* Barras para valores recebidos */}
              {coresEmpresas.map((empresa) => (
                <Bar
                  key={`valor-${empresa.nome}`}
                  yAxisId="left"
                  dataKey={`valor${empresa.nome.replace(/\s+/g, '')}`}
                  fill={empresa.cor}
                  name={`${empresa.nome} - Valor`}
                  opacity={0.7}
                  radius={[2, 2, 0, 0]}
                />
              ))}
              
              {/* Barra consolidada */}
              <Bar
                yAxisId="left"
                dataKey="valorConsolidado"
                fill={dadosGrafico.consolidado.cor}
                name="Consolidado - Valor"
                opacity={0.9}
                radius={[2, 2, 0, 0]}
              />
              
              {/* Linhas para títulos recebidos */}
              {coresEmpresas.map((empresa) => (
                <Line
                  key={`titulos-${empresa.nome}`}
                  yAxisId="right"
                  type="monotone"
                  dataKey={`titulos${empresa.nome.replace(/\s+/g, '')}`}
                  stroke={empresa.cor}
                  strokeWidth={2}
                  name={`${empresa.nome} - ${term.plural}`}
                  dot={{ fill: empresa.cor, strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, stroke: empresa.cor, strokeWidth: 2 }}
                />
              ))}
              
              {/* Linha consolidada */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="titulosConsolidado"
                stroke={dadosGrafico.consolidado.cor}
                strokeWidth={3}
                name={`Consolidado - ${term.plural}`}
                dot={{ fill: dadosGrafico.consolidado.cor, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: dadosGrafico.consolidado.cor, strokeWidth: 2 }}
              />
            </ComposedChart>
          ) : (
            <ComposedChart
              data={dadosProcessados}
              margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" vertical={false} />
              <XAxis 
                dataKey="mes"
                stroke="#94a3b8"
                fontSize={10}
                tickLine={false}
                axisLine={{ stroke: 'rgba(148,163,184,0.35)' }}
                tickFormatter={formatMes}
                padding={{ left: 15, right: 15 }}
              />
              <YAxis
                stroke="#94a3b8"
                fontSize={10}
                domain={[-70, 20]}
                tickFormatter={(value) => `${value}%`}
                tickLine={false}
                axisLine={{ stroke: 'rgba(148,163,184,0.35)' }}
                label={{
                  value: 'Crescimento (%)',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    textAnchor: 'middle',
                    fontSize: 10,
                    fontWeight: 'bold',
                    fill: '#94a3b8'
                  }
                }}
              />

              <defs>
                <linearGradient id="crescimentoPositivo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="crescimentoNegativo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.1} />
                </linearGradient>
              </defs>

              
                  {/* Linhas de crescimento para cada empresa */}
                  {coresEmpresas.map((empresa) => (
                    <Line
                      key={`crescimento-${empresa.nome}`}
                      name={`${empresa.nome} - Crescimento`}
                      type="monotone"
                      dataKey={`crescimento${empresa.nome.replace(/\s+/g, '')}`}
                      stroke={empresa.cor}
                      strokeWidth={3}
                      strokeDasharray="5 5"
                      dot={{ fill: empresa.cor, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: empresa.cor, strokeWidth: 2 }}
                    />
                  ))}
                  
                  {/* Linha de crescimento consolidado */}
                  <Line
                    name="Consolidado - Crescimento"
                    type="monotone"
                    dataKey="crescimentoConsolidado"
                    stroke={dadosGrafico.consolidado.cor}
                    strokeWidth={4}
                    dot={{ fill: dadosGrafico.consolidado.cor, strokeWidth: 3, r: 5 }}
                    activeDot={{ r: 7, stroke: dadosGrafico.consolidado.cor, strokeWidth: 3 }}
                  />
              
              {/* Linha de referência em 0% */}
              <ReferenceLine
                y={0}
                stroke="#94a3b8"
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{ value: "0%", position: "topRight", style: { fontSize: 10, fill: '#94a3b8' } }}
              />
              
              <Tooltip 
                content={<CustomTooltipGrowth />}
                labelFormatter={formatMes}
                labelStyle={{ color: 'var(--text-primary)', fontWeight: 'bold' }}
                contentStyle={{
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxShadow: 'var(--shadow)',
                  color: 'var(--text-primary)'
                }}
              />
              
              <Legend
                content={(props) => <CustomLegend {...props} term={term} />}
                wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GraficoConsolidado;




